import _ from "lodash";
import Task from "folktale/concurrency/task";
import axios from "axios";
import { AUTHLEVEL } from "./utils";
import { appName } from "./app";

// disable pre-flight OPTIONS check for POST data
axios.defaults.headers.post["Content-Type"] =
  "application/x-www-form-urlencoded";

export const ERRORS = Object.freeze({
  userInfoError: Symbol(`Unable to get valid user information`),
  noSourceMessage: `No source supplied`,
  userInfoMessage: `Malformed user info`,
  noResultsMessage: `No results passed`
});

// _ -> Promise.reject string
export const defaultSource = () => Promise.reject(ERRORS.noSourceMessage);

// fn -> Task {errorType: errorMessage} {...userInfo}
const expectedProps = [`username`, `id`, `token`];
export const getUserInfo = (fn = defaultSource) =>
  Task.task(resolver => {
    Promise.resolve(fn())
      .then(userInfo =>
        _.reduce(
          expectedProps,
          (acc, prop) => acc && _.has(userInfo, prop),
          true
        )
          ? !resolver.isCancelled && resolver.resolve(userInfo)
          : !resolver.isCancelled &&
            resolver.reject({ [ERRORS.userInfoError]: ERRORS.userInfoMessage })
      )
      .catch(
        err =>
          !resolver.isCancelled &&
          resolver.reject({ [ERRORS.userInfoError]: err })
      );
  });

// Remember to start XAMPP Apache before freaking out
const apiControllerURL =
  `development` === process.env.NODE_ENV || `test` === process.env.NODE_ENV
    ? `http://localhost:80/controller.php`
    : `https://apps.opengamingnetwork.com/${appName}/app/controller.php`;
// { id, token, ... } => Task Error { id, token, ... }
export const validateCredentials = ({ id = 0, token = ``, ...props }) =>
  Task.task(resolver => {
    axios
      .post(apiControllerURL, {
        action: "validateUser",
        payload: {
          id,
          token
        }
      })
      // expecting { id } on success
      .then(response =>
        response.data.id === id
          ? !resolver.isCancelled &&
            resolver.resolve({
              id: response.data.id,
              token: response.data.token,
              ...props
            })
          : !resolver.isCancelled &&
            resolver.reject(
              new Error(`Unexpected backend response: `, response)
            )
      )
      .catch(error =>
        error.response && error.response.status === 401
          ? !resolver.isCancelled && resolver.resolve({ id, token })
          : !resolver.isCancelled && resolver.reject(error)
      );
  });

// { payload, ...} => Task Error { payload, ... }
export const saveData = ({ payload, ...props }) =>
  Task.task(resolver => {
    axios
      .post(apiControllerURL, {
        action: `saveData`,
        payload
      })
      .then(response =>
        response.data.action === `saveData`
          ? !resolver.isCancelled &&
            resolver.resolve({ payload: response.data.payload, ...props })
          : !resolver.isCancelled &&
            resolver.reject(new Error(`Unexpected backend response`))
      )
      .catch(error => !resolver.isCancelled && resolver.reject(error));
  });

// { field, ...} => Task Error { data, ... }
export const getData = ({ field, ...props }) =>
  Task.task(resolver => {
    axios
      .post(apiControllerURL, {
        action: `getData`,
        payload: { field }
      })
      .then(response =>
        response.data.action === `getData`
          ? !resolver.isCancelled &&
            resolver.resolve({ data: response.data.data, ...props })
          : !resolver.isCancelled &&
            resolver.reject(new Error(`Unexpected backend response`))
      )
      .catch(error => !resolver.isCancelled && resolver.reject(error));
  });

// { groups: []], ...props } => { groups: [], lockedAuths: [Symbol], ...props}
export const addLockedAuths = ({ groups, ...props }) => {
  const lockedAuths = _.cond([
    [groups => _.includes(groups, `${appName}Premium`), () => []],
    [groups => _.includes(groups, `${appName}Users`), () => []],
    [_.stubTrue, () => [AUTHLEVEL.paid]]
  ])(groups);

  return { ...props, groups, lockedAuths };
};

// fn => Task { errorType: errorMessage } { id, loginKey, ... }
export const getAndValidateUser = fn1 =>
  getUserInfo(fn1)
    .chain(credentials => validateCredentials(credentials))
    .map(credentials => addLockedAuths(credentials));

// Task Error {data: { siteMenu: [label, URL], secondaryTopHeadings: [label, URL] }}
export const getMenuItems = Task.task(resolver => {
  axios
    .post(apiControllerURL, {
      action: `getMenu`,
      payload: {}
    })
    .then(response =>
      response.data.action === `getMenu`
        ? !resolver.isCancelled &&
          resolver.resolve({ data: response.data.data })
        : !resolver.isCancelled &&
          resolver.reject(new Error(`Unexpected backend response`))
    )
    .catch(error => !resolver.isCancelled && resolver.reject(error));
});
